import {
	aiProactiveSettingMessages,
	aiProactiveTransformMessages,
} from '@atlaskit/editor-common/ai-messages';
import PanelNoteIcon from '@atlaskit/icon/core/discovery';
import PanelErrorIcon from '@atlaskit/icon/core/error';
import PanelInfoIcon from '@atlaskit/icon/core/information';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import FormatParagraphIcon from '@atlaskit/icon/core/magic-wand';
import NodeIcon from '@atlaskit/icon/core/node';
import TableIcon from '@atlaskit/icon/core/spreadsheet';
import PanelSuccessIcon from '@atlaskit/icon/core/success';
import TextSpellcheckIcon from '@atlaskit/icon/core/text-spellcheck';
import PanelWarnIcon from '@atlaskit/icon/core/warning';
import { fg } from '@atlaskit/platform-feature-flags';

import RephraseIcon from '../../ui/assets/RephraseIcon';
import SuggestedEditIcon from '../../ui/assets/SuggestedEditIcon';
import SuggestedPanelIcon from '../../ui/assets/SuggestedPanelIcon';
import TextBreakIcon from '../../ui/assets/TextBreakIcon';

import type {
	SuggestionSetting,
	RecommendationMatcher,
	RecommendationCard,
} from './suggestion-setting-map';

// Ignored via go/ees007
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
// TODO: Before enabling purging of chunks please ensure that the purged api handler has been implemented
// Maximum number of characters in a single part
export const PART_MAX_SIZE = Number.POSITIVE_INFINITY;
// Maximum number of characters in all parts in a queue
export const TOTAL_PARTS_MAX_SIZE = Number.POSITIVE_INFINITY;
// Maximum number of parts in a queue
export const MAX_PARTS = Number.POSITIVE_INFINITY;
// Maximium number of additional queues to request
export const QUEUE_MAX_ADDITIONAL_ATTEMPTS = 2;
// Limit until we stop tracking count of doc changes
export const DOC_CHANGE_COUNT_LIMIT = 8;
// Minimum size of document's text content to be eligible for proactive AI suggestions
export const DOC_MINIMUM_SIZE_THRESHOLD = 8;
// Width of suggestions panel
export const SUGGESTIONS_PANEL_WIDTH = 320;

export const START_PUNC_CHARS = [
	...Array.from(
		String.fromCharCode(
			39, // single quote
			145, // left single quote
			147, // left double quote
		),
	),
	'(',
	'[',
];

export const END_PUNC_CHARS = [
	Array.from(
		String.fromCharCode(
			34, // double quotes
			39, // single quote
			146, // right single quote
			148, // right double quote
		),
	),
	'.',
	',',
	'!',
	'?',
	')',
	']',
];

export const MIDDLE_PUNC_CHARS = Array.from(
	String.fromCharCode(
		// HYPHENS
		45, // hyphen-minus
		95, // underscore
		150, // en dash
		151, // em dash
		// SINGLE QUOTES
		39, // single quote
		146, // right single quote (apostrophe)
	),
);

export const ANALYTICS_ACTION_SUBJECT_ID = 'proactiveSuggestion';
export const ANALYTICS_FEATURE_NAME = 'Editor AI';
export const ANALYTICS_EXPERIENCE_NAME = 'Proactive AI';

// The number of retry attempts the rate limiter
export const RATE_LIMITER_ATTEMPTS = 3;
// The base delay in milliseconds the rate limiter will start from
export const RATE_LIMITER_DELAY = 1000;
//
export const RATE_LIMITER_JITTER = 2000;

/**
 * Setting keys for the suggestion settings
 */
export enum SETTING_KEYS {
	ALL_SUGGESTIONS = 'displayAllSuggestions',
	ALL_PANELS = 'displayAllPanelSuggestions',
	CONVERT_TO_ERROR_PANEL = 'displayErrorPanel',
	CONVERT_TO_INFO_PANEL = 'displayInfoPanel',
	CONVERT_TO_NOTE_PANEL = 'displayNotePanel',
	CONVERT_TO_SUCCESS_PANEL = 'displaySuccessPanel',
	CONVERT_TO_WARN_PANEL = 'displayWarningPanel',
	CONVERT_TO_TABLE = 'displayTable',
	SPLIT_PARAGRAPH = 'displaySplitParagraph',
	FORMAT_PARAGRAPH = 'displayFormatParagraph',
	UNKNOWN = 'unknownRecommendation',
}

/**
 * Array of suggestion setting items
 */
const SETTING_RECOMMENDATIONS_MAPPING: SuggestionSetting[] = [
	{
		Icon: SuggestedEditIcon,
		settingKey: SETTING_KEYS.ALL_SUGGESTIONS,
		title: aiProactiveSettingMessages.toggleAllSuggestionsTitle,
		description: aiProactiveSettingMessages.toggleAllSuggestionsDescription,
	},
	{
		Icon: FormatParagraphIcon,
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
		title: aiProactiveSettingMessages.toggleFormatParagraphTitle,
		description: aiProactiveSettingMessages.toggleFormatParagraphDescription,
		parentSetting: SETTING_KEYS.ALL_SUGGESTIONS,
	},
	{
		Icon: SuggestedPanelIcon,
		settingKey: SETTING_KEYS.ALL_PANELS,
		title: aiProactiveSettingMessages.toggleAllPanelSuggestionsTitle,
		description: aiProactiveSettingMessages.toggleAllPanelSuggestionsDescription,
		parentSetting: SETTING_KEYS.ALL_SUGGESTIONS,
	},
	{
		Icon: PanelErrorIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_ERROR_PANEL,
		title: aiProactiveSettingMessages.toggleErrorPanelTitle,
		parentSetting: SETTING_KEYS.ALL_PANELS,
	},
	{
		Icon: PanelInfoIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_INFO_PANEL,
		title: aiProactiveSettingMessages.toggleInfoPanelTitle,
		parentSetting: SETTING_KEYS.ALL_PANELS,
	},
	{
		Icon: PanelNoteIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_NOTE_PANEL,
		title: aiProactiveSettingMessages.toggleNotePanelTitle,
		parentSetting: SETTING_KEYS.ALL_PANELS,
	},
	{
		Icon: PanelSuccessIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_SUCCESS_PANEL,
		title: aiProactiveSettingMessages.toggleSuccessPanelTitle,
		parentSetting: SETTING_KEYS.ALL_PANELS,
	},
	{
		Icon: PanelWarnIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_WARN_PANEL,
		title: aiProactiveSettingMessages.toggleWarningPanelTitle,
		parentSetting: SETTING_KEYS.ALL_PANELS,
	},
	{
		Icon: TextBreakIcon,
		settingKey: SETTING_KEYS.SPLIT_PARAGRAPH,
		title: aiProactiveSettingMessages.toggleSplitParagraphTitle,
		description: aiProactiveSettingMessages.toggleSplitParagraphDescription,
		parentSetting: SETTING_KEYS.ALL_SUGGESTIONS,
	},
	{
		Icon: TableIcon,
		settingKey: SETTING_KEYS.CONVERT_TO_TABLE,
		title: aiProactiveSettingMessages.toggleTableTitle,
		description: aiProactiveSettingMessages.toggleTableDescription,
		parentSetting: SETTING_KEYS.ALL_SUGGESTIONS,
	},
];

export const defaultRecommendationCard: RecommendationCard = {
	title: aiProactiveTransformMessages.recommendationDefaultTitle,
	description: aiProactiveTransformMessages.recommendationDefaultDescription,
	// eslint-disable-next-line @atlaskit/editor/no-re-export
	icon: NodeIcon,
};

/**
 * Array of recommendation display types.
 * The order of elments defines the matching order. The first recommendation that matches the details will be used.
 * Based on https://hello.atlassian.net/wiki/spaces/AM3/pages/4173571632/Proactive+AI+Trigger+API+Design#Supported-Transformations-and-Recommendations .
 */
const RECOMMENDATION_DISPLAY_TYPES: RecommendationMatcher[] = [
	{
		title: aiProactiveTransformMessages.recommendationConvertToErrorPanelTitle,
		description: aiProactiveTransformMessages.recommendationConvertToErrorPanelDescription,
		icon: PanelErrorIcon,
		transformAction: 'CONVERT_TO_ERROR_PANEL',
		type: 'CONVERT_TO_PANEL',
		details: {
			panel_type: 'ERROR',
		},
		settingKey: SETTING_KEYS.CONVERT_TO_ERROR_PANEL,
	},
	{
		title: aiProactiveTransformMessages.recommendationConvertToInfoPanelTitle,
		description: aiProactiveTransformMessages.recommendationConvertToInfoPanelDescription,
		icon: PanelInfoIcon,
		transformAction: 'CONVERT_TO_INFO_PANEL',
		type: 'CONVERT_TO_PANEL',
		details: {
			panel_type: 'INFO',
		},
		settingKey: SETTING_KEYS.CONVERT_TO_INFO_PANEL,
	},
	{
		title: aiProactiveTransformMessages.recommendationConvertToNotePanelTitle,
		description: aiProactiveTransformMessages.recommendationConvertToNotePanelDescription,
		icon: PanelNoteIcon,
		transformAction: 'CONVERT_TO_NOTE_PANEL',
		type: 'CONVERT_TO_PANEL',
		details: {
			panel_type: 'NOTE',
		},
		settingKey: SETTING_KEYS.CONVERT_TO_NOTE_PANEL,
	},
	{
		title: aiProactiveTransformMessages.recommendationConvertToSuccessPanelTitle,
		description: aiProactiveTransformMessages.recommendationConvertToSuccessPanelDescription,
		icon: PanelSuccessIcon,
		transformAction: 'CONVERT_TO_SUCCESS_PANEL',
		type: 'CONVERT_TO_PANEL',
		details: {
			panel_type: 'SUCCESS',
		},
		settingKey: SETTING_KEYS.CONVERT_TO_SUCCESS_PANEL,
	},
	{
		title: aiProactiveTransformMessages.recommendationConvertToWarnPanelTitle,
		description: aiProactiveTransformMessages.recommendationConvertToWarnPanelDescription,
		icon: PanelWarnIcon,
		transformAction: 'CONVERT_TO_WARN_PANEL',
		type: 'CONVERT_TO_PANEL',
		details: {
			panel_type: 'WARN',
		},
		settingKey: SETTING_KEYS.CONVERT_TO_WARN_PANEL,
	},
	{
		title: aiProactiveTransformMessages.recommendationSplitParagraphTitle,
		description: aiProactiveTransformMessages.recommendationSplitParagraphDescription,
		icon: TextBreakIcon,
		transformAction: 'SPLIT_PARAGRAPH',
		type: 'SPLIT_PARAGRAPH',
		settingKey: SETTING_KEYS.SPLIT_PARAGRAPH,
	},
	{
		title: aiProactiveTransformMessages.recommendationConvertToTableTitle,
		description: aiProactiveTransformMessages.recommendationConvertToTableDescription,
		icon: TableIcon,
		transformAction: 'CONVERT_TO_TABLE',
		type: 'CONVERT_TO_TABLE',
		settingKey: SETTING_KEYS.CONVERT_TO_TABLE,
	},
	{
		title: aiProactiveTransformMessages.recommendationFormatParagraphClarityTitle,
		description: aiProactiveTransformMessages.recommendationFormatParagraphClarityDescription,
		icon: RephraseIcon,
		type: 'FORMAT_PARAGRAPH',
		details: {
			writing_fixes: ['CLARITY_CONCISENESS', 'GRAMMAR_SYNTAX'],
		},
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
	},
	{
		title: aiProactiveTransformMessages.recommendationFormatParagraphListTitle,
		description: aiProactiveTransformMessages.recommendationFormatParagraphListDescription,
		icon: ListBulletedIcon,
		type: 'FORMAT_PARAGRAPH',
		details: {
			writing_fixes: ['BULLETING_NUMBERING'],
		},
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
	},
	{
		title: aiProactiveTransformMessages.recommendationFormatParagraphStructureTitle,
		description: aiProactiveTransformMessages.recommendationFormatParagraphStructureDescription,
		icon: TextBreakIcon,
		type: 'FORMAT_PARAGRAPH',
		details: {
			writing_fixes: ['PARAGRAPH_SPLIT', 'HEADINGS'],
		},
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
	},
	{
		title: aiProactiveTransformMessages.recommendationFormatParagraphStructureTitle,
		description: aiProactiveTransformMessages.recommendationFormatParagraphStructureDescription,
		icon: TextSpellcheckIcon,
		type: 'FORMAT_PARAGRAPH',
		details: {
			writing_fixes: ['SPELLING'],
		},
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
	},
	{
		// Catch all for FORMAT_PARAGRAPH. Must be the last item with type 'FORMAT_PARAGRAPH'.
		title: aiProactiveTransformMessages.recommendationFormatParagraphTitle,
		description: aiProactiveTransformMessages.recommendationFormatParagraphDescription,
		icon: FormatParagraphIcon,
		transformAction: 'FORMAT_PARAGRAPH',
		type: 'FORMAT_PARAGRAPH',
		settingKey: SETTING_KEYS.FORMAT_PARAGRAPH,
	},
];

/**
 * Add any new WIP/FF suggestion setting here
 * as you're not allowed to evaluate fg at a module level
 *
 *  aix_proactive_format_paragraph_feature_enabled -> add FORMAT_PARAGRAPH and get rid of SPLIT_PARAGRAPH
 * 	https://hello.atlassian.net/wiki/spaces/AM3/pages/edit-v2/4521721900?focusedCommentId=4690745524
 */
export const getSuggestionSettingArray = (): SuggestionSetting[] =>
	SETTING_RECOMMENDATIONS_MAPPING.filter((sugg) =>
		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
		fg('aix_proactive_format_paragraph_feature_enabled')
			? sugg.settingKey !== SETTING_KEYS.SPLIT_PARAGRAPH
			: sugg.settingKey !== SETTING_KEYS.FORMAT_PARAGRAPH,
	);

export const getRecommendationMatcherArray = (): RecommendationMatcher[] =>
	RECOMMENDATION_DISPLAY_TYPES.filter((sugg) =>
		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
		fg('aix_proactive_format_paragraph_feature_enabled')
			? sugg.transformAction !== 'SPLIT_PARAGRAPH'
			: sugg.transformAction !== 'FORMAT_PARAGRAPH',
	);
